import {
  Component,
  ComponentInterface,
  Event,
  EventEmitter,
  h,
  Prop,
  State,
  Watch,
} from '@stencil/core';

import timeTableEvents from '../../app/paf-timetable/paf-timetable.events';

@Component({
  shadow: true,
  styleUrl: 'paf-journey-summary.scss',
  tag: 'paf-journey-summary',
})

export class PafJourneySummary implements ComponentInterface {

  /**
   * The text to show as origin of the summary title
   */
  @Prop() public origin!: string;

  /**
   * The text to show as destination of the summary title
   */
  @Prop() public destination!: string;

  /**
   * The text to show as via of the summary title
   */
  @Prop() public via?: string;

  /**
   * The text to show as via description
   */
  @Prop() public viaDescription?: string;

  /**
   * The date to show in the details
   */
  @Prop() public date!: string;

  /**
   * String to use for departure or arrival in the details
   */
  @Prop() public departureArrival!: string;

  /**
   * The time in the details
   */
  @Prop() public time!: string;

  /**
   * Text to show on the button for changing the search
   */
  @Prop() public changeButtonText!: string;

  /**
   * The hidden text to announce to screenreaders instead of the title
   */
  @Prop() public assistiveTextTitle!: string;

  /**
   * The hidden text to announce to screenreaders instead of the details
   */
  @Prop() public assistiveTextDetails!: string;

  /**
   * The heading level for the summary
   */
  @Prop() public headingLevel!: number;

  /**
   * If set to true, the component will show a loading skeleton
   */
  @Prop() public loading?: boolean;

  @State() private _display = false;

  @Watch('loading')
  public watchPropHandler(newValue: boolean): void {
    if (!newValue) {
      this._setFocusOnAssistiveTitleText();
    }
  }

  /**
   * Event which will be emitted if the change-button is clicked
   */
  @Event({
    bubbles: true,
    composed: true,
    eventName: 'paf-journey-summary_change',
  })
  public change: EventEmitter<void>;

  private _heading: HTMLHeadingElement;
  private _scrollTimeout: ReturnType<typeof setTimeout>;

  private _parseAssistiveTextTitle = (): string => {
    let assistiveText = this.assistiveTextTitle
      .replace('{origin}', this.origin)
      .replace('{destination}', this.destination);

    if (this.via) {
      assistiveText += ` ${this.viaDescription} ${this.via}.`;
    }

    return assistiveText;
  };

  private _parseAssistiveTextDetails = (): string => (
    this.assistiveTextDetails
      .replace('{date}', this.date)
      .replace('{time}', this.time)
  );

  private _isVisibleInViewport = (element: HTMLElement): boolean => {
    const rect = element.getBoundingClientRect();

    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  };

  private _setFocusOnAssistiveTitleText = (): void => {
    this._heading.setAttribute('tabindex', '-1');

    if (this._isVisibleInViewport(this._heading)) {
      this._heading.focus();
    } else {
      document.addEventListener('scroll', this._setupScrollTimeOut);
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this._heading.scrollIntoView({
        behavior: prefersReducedMotion
          ? 'auto'
          : 'smooth',
        block: 'start',
      });
    }
  };

  private _setupScrollTimeOut = (): void => {
    clearTimeout(this._scrollTimeout);
    this._scrollTimeout = setTimeout(() => {
      this._heading.focus();
      document.removeEventListener('scroll', this._setupScrollTimeOut);
    }, 100);
  };

  private _removeTabIndex = (): void => {
    this._heading.removeAttribute('tabindex');
  };

  /**
   * We use this event to avoid clashing focus
   * handlings in tests where items are loaded
   * and then a row gets expanded
   */
  private _cancelFocusEvent = (): void => {
    clearTimeout(this._scrollTimeout);
    document.removeEventListener('scroll', this._setupScrollTimeOut);
  };

  public componentWillLoad(): void {
    this._display = true;
  }

  public componentDidLoad(): void {
    document.addEventListener(timeTableEvents.cancelFocusOnSummaryTitle, this._cancelFocusEvent);
  }

  public disconnectedCallback(): void {
    document.removeEventListener(timeTableEvents.cancelFocusOnSummaryTitle, this._cancelFocusEvent);
  }

  private _onChange = (evt): void => {
    evt.preventDefault();
    this.change.emit();
  };

  public render(): HTMLDivElement {
    const HEADING_TAG = `h${this.headingLevel}`;
    const headingAriaLabel = `${this._parseAssistiveTextTitle()} ${this._parseAssistiveTextDetails()}`;
    let attributes = {};

    if (this.loading) {
      attributes = {
        'aria-hidden': true,
        'role': 'presentation',
      };
    }

    return (
      <div
        class={{
          loading: this.loading,
          summary: true,
          visible: this._display,
        }}
        {...attributes}
      >
        <div
          class='title'
        >
          {this.loading &&
            <div class='loader'></div>
          }

          <HEADING_TAG
            aria-label={headingAriaLabel}
            class='title-wrapper'
            data-testid='summary-title'
            ref={(elem) => {
              this._heading = elem;
            }}
            onBlur={this._removeTabIndex}
          >
            {this.origin}
            <svg class='icon' viewBox='0 0 49 34'><path d='M31.5 0h-2.8l16 16H.5v2h44.2l-16 16h2.8l17-17-17-17Z'/></svg>
            {this.destination}
          </HEADING_TAG>
        </div>

        <p class={{
          details: true,
          loading: this.loading,
        }}>
          {this.loading &&
            <div class='loader'></div>
          }
          <span
            class='details-wrapper'
            data-testid='title'
          >
            <span
              class='details-visible'
              aria-hidden='true'
              role='presentation'
            >
              {this.via &&
                <span class='via'>{this.viaDescription} {this.via}. </span>
              }
              {this.date}
              <span class='details-time'>{this.departureArrival} {this.time}</span>
            </span>
            <button
              class='details-change'
              onClick={this._onChange}
              data-testid='button'
              type='button'
            >{this.changeButtonText}</button>
          </span>
        </p>
      </div>
    );
  }
}
